// components/CustomDrawerContent.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logout } from "@/store/slices/authSlice";
import { Ionicons } from "@expo/vector-icons";
import { router, usePathname } from "expo-router";
import {
  Alert,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import AppScreen from "./AppScreen";

export default function CustomDrawerContent({ navigation }: any) {
  const { colors, spacing } = useAppTheme();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const userType = user?.userType; // "Owner" or "Employee"

  const handleLogout = () => {
    Alert.alert("Logout", "Are you sure?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Logout",
        style: "destructive",
        onPress: async () => {
          await dispatch(logout());
          router.replace("/(auth)/login");
        },
      },
    ]);
  };

  const handleSettings = () => {
    router.push("/(app)/settings");
    navigation.closeDrawer(); // Close drawer after navigation
  };

  const navigateTo = async (screen: string) => {
    router.push(`/(app)/${screen}` as any);
    navigation.closeDrawer();
  };

  // Define all possible screens with their required user roles
  const allScreens = [
    {
      name: "dashboard",
      title: "Dashboard",
      icon: "home-outline",
      roles: ["Owner", "Employee"],
    },
    {
      name: "employees",
      title: "Employees",
      icon: "people-outline",
      roles: ["Owner"],
    }, // Only Owner
    {
      name: "customers",
      title: "Customers",
      icon: "person-outline",
      roles: ["Owner", "Employee"],
    },
    {
      name: "inventory",
      title: "Inventory",
      icon: "cube-outline",
      roles: ["Owner", "Employee"],
    },
    {
      name: "invoices",
      title: "Invoices",
      icon: "document-text-outline",
      roles: ["Owner", "Employee"],
    },
    {
      name: "reports",
      title: "Reports",
      icon: "bar-chart-outline",
      roles: ["Owner", "Employee"],
    }, // Optional: restrict to Owner
    {
      name: "payments",
      title: "Payments",
      icon: "cash-outline",
      roles: ["Owner", "Employee"],
    }, // Optional: restrict to Owner
  ];

  // Filter screens based on user type (default to Employee if not set)
  const mainScreens = allScreens.filter((screen) =>
    screen.roles.includes(userType || "Employee"),
  );

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    header: {
      paddingVertical: spacing.xl,
      paddingHorizontal: spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      alignItems: "center",
      marginBottom: spacing.md,
    },
    logo: { width: 70, height: 70, borderRadius: 35, marginBottom: spacing.sm },
    appName: { fontSize: 18, fontWeight: "600", color: colors.text },
    userInfo: { marginTop: spacing.sm, alignItems: "center" },
    userName: { fontSize: 14, fontWeight: "500", color: colors.text },
    userRole: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    drawerItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      marginHorizontal: spacing.sm,
      borderRadius: 8,
      marginVertical: 2,
    },
    activeDrawerItem: {
      backgroundColor: colors.primary + "20", // 20% opacity
    },
    drawerIcon: { marginRight: spacing.md },
    drawerLabel: { fontSize: 14, color: colors.text },
    activeLabel: { color: colors.primary, fontWeight: "500" },
    bottomSection: {
      marginTop: "auto",
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingVertical: spacing.md,
    },
    bottomItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      marginHorizontal: spacing.sm,
      borderRadius: 8,
    },
    bottomText: { fontSize: 16, marginLeft: spacing.md },
    logoutText: { color: colors.danger },
    settingsText: { color: colors.text },
  });

  const isActive = (screenName: string) => pathname === `/(app)/${screenName}`;

  return (
    <AppScreen>
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          <View style={styles.header}>
            <Image
              source={require("@/assets/images/icon.png")}
              style={styles.logo}
            />
            <Text style={styles.appName}>Shree Setup</Text>
            {user && (
              <View style={styles.userInfo}>
                <Text style={styles.userName}>{user.ownerName}</Text>
                <Text style={styles.userRole}>{user.userType}</Text>
              </View>
            )}
          </View>

          {mainScreens.map((screen) => (
            <TouchableOpacity
              key={screen.name}
              style={[
                styles.drawerItem,
                isActive(screen.name) && styles.activeDrawerItem,
              ]}
              onPress={() => navigateTo(screen.name)}
            >
              <Ionicons
                name={screen.icon as any}
                size={24}
                color={
                  isActive(screen.name) ? colors.primary : colors.textLight
                }
                style={styles.drawerIcon}
              />
              <Text
                style={[
                  styles.drawerLabel,
                  isActive(screen.name) && styles.activeLabel,
                ]}
              >
                {screen.title}
              </Text>
            </TouchableOpacity>
          ))}

          <View style={styles.bottomSection}>
            <TouchableOpacity
              style={styles.bottomItem}
              onPress={handleSettings}
            >
              <Ionicons name="settings-outline" size={24} color={colors.text} />
              <Text style={[styles.bottomText, styles.settingsText]}>
                Settings
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.bottomItem} onPress={handleLogout}>
              <Ionicons
                name="log-out-outline"
                size={24}
                color={colors.danger}
              />
              <Text style={[styles.bottomText, styles.logoutText]}>Logout</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </AppScreen>
  );
}
